import { z } from 'zod';
import { PaginationQueryParamsSchema } from '@terra/shared-dto-types';
import {
  ApplicationProtocolSchema,
  DnsSourceTypeSchema,
  GatewayProviderCategorySchema,
  GatewaySourceTypeSchema,
  NetblockSourceTypeSchema,
  ScopeSchema,
  SoftwareOwnershipSchema,
  SoftwareTypeSchema,
  TransportProtocolSchema,
} from '@terra/shared-schemas-orchestrator';
import {
  externalAssetListGroupsField,
  externalAssetListOrderingFields,
} from './external-asset-list-ordering.schema';
import {
  HostDtoSchema,
  NetworkServiceDtoSchema,
} from './external-network-asset.schema';
import {
  DomainDtoSchema,
  GatewayListItemSchema,
  NetBlockDtoSchema,
  SoftwareDtoSchema,
} from './external-network.schema';

const ScopeFilterSchema = ScopeSchema.optional().describe('Filter by scope');
const SearchSchema = z.string().optional().describe('Free-text search');
const CreatedAtListFilterFields = {
  createdStartDate: z.coerce.date().optional(),
  createdEndDate: z.coerce.date().optional(),
};
const SoftwareRelationListFilterFields = {
  product: z.string().optional(),
  softwareType: SoftwareTypeSchema.optional(),
  cveId: z.string().optional(),
};

export const NetBlockListQuerySchema = PaginationQueryParamsSchema.extend({
  ...externalAssetListOrderingFields('NETBLOCK'),
  scope: ScopeFilterSchema,
  sourceType: NetblockSourceTypeSchema.optional(),
  search: SearchSchema,
  region: z.string().optional(),
  organization: z.string().optional(),
  ...CreatedAtListFilterFields,
});
export const NetBlockListResponseSchema = z.object({
  data: z.array(NetBlockDtoSchema),
  ancestors: z.array(NetBlockDtoSchema).default([]),
  total: z.number(),
  ...externalAssetListGroupsField,
});

export const DomainListQuerySchema = PaginationQueryParamsSchema.extend({
  ...externalAssetListOrderingFields('DOMAIN'),
  scope: ScopeFilterSchema,
  sourceType: DnsSourceTypeSchema.optional(),
  isTopLevel: z
    .union([
      z.literal('true').transform(() => true as const),
      z.literal('false').transform(() => false as const),
      z.boolean(),
    ])
    .optional(),
  search: SearchSchema,
  ...SoftwareRelationListFilterFields,
  ...CreatedAtListFilterFields,
  resolvedCloudProvider: z
    .string()
    .optional()
    .describe(
      'Filter domains attributed to a cloud provider via resolved hosts',
    ),
});
export const DomainListResponseSchema = z.object({
  data: z.array(DomainDtoSchema),
  total: z.number(),
  ...externalAssetListGroupsField,
});

export const HostListQuerySchema = PaginationQueryParamsSchema.extend({
  ...externalAssetListOrderingFields('HOST'),
  netblockId: z.string().optional(),
  cloudProvider: z.string().optional(),
  hasCloudProvider: z
    .union([
      z.literal('true').transform(() => true as const),
      z.literal('false').transform(() => false as const),
      z.boolean(),
    ])
    .optional(),
  countryCode: z.string().optional(),
  hasCountry: z
    .union([
      z.literal('true').transform(() => true as const),
      z.literal('false').transform(() => false as const),
      z.boolean(),
    ])
    .optional(),
  search: SearchSchema,
  ...SoftwareRelationListFilterFields,
  ...CreatedAtListFilterFields,
  scope: ScopeFilterSchema,
});
export const HostListResponseSchema = z.object({
  data: z.array(HostDtoSchema),
  total: z.number(),
  ...externalAssetListGroupsField,
});

export const GatewayListQuerySchema = PaginationQueryParamsSchema.extend({
  ...externalAssetListOrderingFields('GATEWAY'),
  providerCategory: GatewayProviderCategorySchema.optional(),
  sourceType: GatewaySourceTypeSchema.optional(),
  scope: ScopeFilterSchema,
  search: SearchSchema,
  softwareType: SoftwareTypeSchema.optional(),
  ...CreatedAtListFilterFields,
});
export const GatewayListResponseSchema = z.object({
  data: z.array(GatewayListItemSchema),
  total: z.number(),
  ...externalAssetListGroupsField,
});

export const NetworkServiceListQuerySchema = PaginationQueryParamsSchema.extend(
  {
    ...externalAssetListOrderingFields('NETWORK_SERVICE'),
    search: SearchSchema,
    hostId: z.string().optional(),
    gatewayId: z.string().optional(),
    port: z.coerce.number().int().optional(),
    transportProtocol: TransportProtocolSchema.optional(),
    applicationProtocol: ApplicationProtocolSchema.optional(),
    tlsEnabled: z
      .union([
        z.literal('true').transform(() => true as const),
        z.literal('false').transform(() => false as const),
        z.boolean(),
      ])
      .optional(),
    ...SoftwareRelationListFilterFields,
    ...CreatedAtListFilterFields,
    scope: ScopeFilterSchema,
  },
);
export const NetworkServiceListResponseSchema = z.object({
  data: z.array(NetworkServiceDtoSchema),
  total: z.number(),
  ...externalAssetListGroupsField,
});

export const SoftwareListQuerySchema = PaginationQueryParamsSchema.extend({
  ...externalAssetListOrderingFields('SOFTWARE'),
  hostId: z.string().optional(),
  networkServiceId: z.string().optional(),
  softwareType: SoftwareTypeSchema.optional(),
  ownership: SoftwareOwnershipSchema.optional(),
  isCustomWebApp: z
    .union([
      z.literal('true').transform(() => true as const),
      z.literal('false').transform(() => false as const),
      z.boolean(),
    ])
    .optional(),
  vendor: z.string().optional(),
  product: z.string().optional(),
  cveId: z.string().optional(),
  search: SearchSchema,
  ...CreatedAtListFilterFields,
});
export const SoftwareListResponseSchema = z.object({
  data: z.array(SoftwareDtoSchema),
  total: z.number(),
  ...externalAssetListGroupsField,
});
